'use strict';

import { settings } from '..';
import { translate } from '../Translator';
import { repositoryActions } from '../actions/actions';
import { extractDataFromJwt, UserIdentity } from '../../util';

function transformUrlOption(urlOption) {
  if (!urlOption) {
    return undefined;
  }
  return Array.isArray(urlOption) ? urlOption.join('/') : urlOption;
}

/**
 * Will handle 403 & 500 server errors, by displaying a message on the screen that either validation failed or that there was a server error
 * Will rethrow all other exceptions
 * @returns {object} method delegate taking an error argument, typically used to handle the catch block of a promise
 */
function handleServiceFailure() {
  return (err) => {
    if (err && err.response && err.response.status) {
      // the user should be informed there was an error in the service
      let message = {
        messageType: 'warning',
        messageText: 'An error occured on the server: ' + err.response.statusText,
        timeout: 0,
        id: new Date().toTimeString()
      };
      switch (err.response.status) {
        case 403:
          // server will throw 403 when the authentication / validation failed
          message.messageText = translate('Your current session has expired, or the authentication service cannot be reached!');
          return {
            type: repositoryActions('message').SET,
            item: message
          };
        case 500:
          return {
            type: repositoryActions('message').SET,
            item: message
          };
      }
    }
    // throw when it wasn't a 403 or 500 service error
    throw err;
  };
}

/**
 * Will handle any 401 error (Unauthorized) to retrigger the authentication with the hash saved inside the sessionStorage (which happens at 
 * authentication time). In case it was succesful, it will store the new token in the sessionStorage and update the settings.auth
 * If it was not succesful, it will erase any of the session storage data and reload the portal (showing the NotAuthorized exception in case it cannot
 * re-authenticate)
 * This doesn't impact the original re-authentication request that is part of the identityReducer
 * @param {string} method the original method that was used in the service request
 * @param {string} endPoint the target endPoint that was used for the service request
 * @param  {[any]} args all the arguments that were originally attached
 * @returns {delegate} catch part for the error response
 */
function handleAuthenticationAndReplayAction(method, endPoint, ...args) {
  return (err) => {
    if (err.response.status === 401) {
      return service('identity').get(['get', UserIdentity.token])
        .then(response => {
          const payload = extractDataFromJwt(response.data);
          settings.auth = payload.content;
          UserIdentity.jwt = response.data;
          // replay the action in the end
          return service(endPoint)[method](...args);
        })
        .catch(() => {
          // reauthenticate failed, so, delete authentication
          delete settings.auth;
          // remove the token
          UserIdentity.jwt = null;
          // remove the hash
          UserIdentity.token = null;
          // and reload the portal
          document.location.reload(true);
        });
    }
    // don't break any previous throws
    throw err;
  };
}

async function handleFetchResponse(response) {
  if (!response.ok) {
    throw { response: { status: response.status, statusText: response.statusText, error: response.error } };
  }
  const { headers } = response;
  const contentType = headers && headers.has('Content-Type') && headers.get('Content-Type');
  if (!headers || !contentType || contentType.includes('application/json')) {
    return { data: await response.json() };
  }
  if (contentType.includes('text/plain')) {
    return { data: await response.text() };
  }
  return { data: await response.blob() };
}

export const service = endPoint => ( {
  get( urlOption, args ) {
    const url = settings.transformApiRoute( settings.combineUrlParts( endPoint, transformUrlOption( urlOption ) ) );
    return fetch( url, settings.getConfig( args, { method: 'GET' } ) ) // get
      .then( handleFetchResponse )
      .catch( err => {throw err} )
      .catch ( handleAuthenticationAndReplayAction( 'get', endPoint, ...arguments ) );
  },
  post(data, urlOption, args) {
    const url = settings.transformApiRoute(settings.combineUrlParts(endPoint, transformUrlOption(urlOption)));
    return fetch(url, settings.getConfig(args, { method: 'POST' }, { body: data })) // post
      .then(handleFetchResponse)
      .catch(handleServiceFailure())
      .catch(handleAuthenticationAndReplayAction('post', endPoint, ...arguments));
  },
  getFile(data, urlOption, args, method) {
    // const url = settings.transformRoute( endPoint );
    // return fetch(endPoint, {headers: args, method: method, body: JSON.stringify(data) }) // post
    //   .then(handleFetchResponse)
    //   .catch(handleServiceFailure())
    //   .catch(handleAuthenticationAndReplayAction('post', endPoint, ...arguments));
    return fetch(endPoint, settings.getConfig(args, {method: method}, {body: data})) // post
      .then(handleFetchResponse)
      .catch(handleServiceFailure())
      .catch(handleAuthenticationAndReplayAction(method, endPoint, ...arguments));
  },
  remove(urlOption, args) {
    const url = settings.transformApiRoute(settings.combineUrlParts(endPoint, transformUrlOption(urlOption)));
    return fetch(url, settings.getConfig(args, { method: 'DELETE' })) // delete
      .then(handleFetchResponse)
      .catch(handleServiceFailure())
      .catch(handleAuthenticationAndReplayAction('delete', endPoint, ...arguments));
  },
  add(data, urlOption, args) {
    const url = settings.transformApiRoute(settings.combineUrlParts(endPoint, transformUrlOption(urlOption)));
    return fetch(url, settings.getConfig(args, { method: 'PUT', body: JSON.stringify(data) })) // put
      .then(handleFetchResponse)
      .catch(handleServiceFailure())
      .catch(handleAuthenticationAndReplayAction('add', endPoint, ...arguments));
  },
  customRequest(requestMethod, url, data, config) {
    if (!['get', 'put', 'post', 'delete', 'head'].includes(requestMethod) || !url) {
      throw 'Invalid request parameters';
    }

    return fetch(url, settings.getConfig(config, { method: requestMethod, body: config.xmlContent ? data : data && JSON.stringify(data) }))
      .then(handleFetchResponse)
      .catch(handleServiceFailure())
      .catch(handleAuthenticationAndReplayAction('customRequest', endPoint, ...arguments));
  }
});